import composeRefs from '@jetbrains/ring-ui/components/global/composeRefs'
import classNames from 'classnames'
import {iframeResizer} from 'iframe-resizer'
import * as React from 'react'
import type {IframeHTMLAttributes, Ref} from 'react'

import {noop} from '../../../utils/empty'

import styles from './IframeAdapter.css'

type Props = IframeHTMLAttributes<HTMLIFrameElement> & {
  src: string
  onIframeLoaded?: () => unknown
  onIframeUnload?: () => unknown
  onURLChange?: (newURL: string) => unknown
  autoHeight?: boolean
  iframeRef?: Ref<HTMLIFrameElement>
  preventFlashing?: boolean
}

const WAIT_FOR_LOADING_TIMEOUT = 2000
const POLLING_URL_INTERVAL = 100

declare global {
  interface Window {
    HTMLAnchorElement: typeof HTMLAnchorElement
    Node: typeof Node
  }
}

function IframeAdapter({
  className,
  src,
  onIframeLoaded = noop,
  onIframeUnload = noop,
  onURLChange = noop,
  title,
  autoHeight = false,
  iframeRef,
  preventFlashing,
  ...restProps
}: Props) {
  const [hidden, setHidden] = React.useState(preventFlashing)
  const iframeInnerRef = React.useRef<HTMLIFrameElement>(null)
  const detectURLChangeTimer = React.useRef<number>()
  const detectUrlChange = React.useCallback(() => {
    const currentUrl = iframeInnerRef.current?.contentDocument
      ? iframeInnerRef.current.contentDocument.location.toString()
      : null

    if (currentUrl != null && currentUrl !== src) {
      onIframeUnload()
      onURLChange(currentUrl)
      clearInterval(detectURLChangeTimer.current)
    }
  }, [onIframeUnload, onURLChange, src])
  const startDetectingUrlChange = React.useCallback(() => {
    clearInterval(detectURLChangeTimer.current)
    detectURLChangeTimer.current = window.setInterval(detectUrlChange, POLLING_URL_INTERVAL)
  }, [detectUrlChange])
  React.useEffect(() => () => clearInterval(detectURLChangeTimer.current), [])
  const hashChangeHandler = React.useCallback(() => {
    const currentUrl = iframeInnerRef.current?.contentDocument?.location.toString()
    if (currentUrl != null) {
      onURLChange(currentUrl)
    }
  }, [onURLChange])
  const iframeLoadedHandler = React.useCallback(() => {
    const iframeEl = iframeInnerRef.current
    if (iframeEl == null) {
      return
    }

    setHidden(false)
    onIframeLoaded()
    if (autoHeight) {
      iframeEl.style.height = '0px'
    }
    const {contentWindow, contentDocument} = iframeEl
    if (contentWindow == null) {
      return
    }

    contentWindow.addEventListener('hashchange', hashChangeHandler)
    contentWindow.addEventListener('beforeunload', startDetectingUrlChange)

    if (contentDocument?.body != null) {
      contentDocument?.body.addEventListener(
        'click',
        (event: MouseEvent) => {
          let target = event.target

          while (target instanceof contentWindow.Node) {
            if (
              target instanceof contentWindow.HTMLAnchorElement &&
              target.href != null &&
              target.href !== '' &&
              target.target !== '_self'
            ) {
              if (iframeInnerRef.current != null) {
                iframeInnerRef.current.contentWindow?.removeEventListener(
                  'beforeunload',
                  startDetectingUrlChange,
                )
                iframeInnerRef.current.contentWindow?.removeEventListener(
                  'hashchange',
                  hashChangeHandler,
                )
              }

              break
            }

            target = target.parentElement
          }
        },
        true,
      )
      const mainContentElement = contentDocument.getElementById('mainContent')
      const stickyWrapperElement = contentDocument.getElementById('sticky-wrapper')

      if (mainContentElement && autoHeight) {
        mainContentElement.style.overflowX = 'auto'
        mainContentElement.dataset.iframeHeight = 'true'
      }

      if (stickyWrapperElement) {
        stickyWrapperElement.style.display = 'none'
      }
    }
  }, [onIframeLoaded, autoHeight, hashChangeHandler, startDetectingUrlChange])
  React.useEffect(() => {
    if (autoHeight) {
      const iframeEl = iframeInnerRef.current

      if (iframeEl == null) {
        return undefined
      }

      iframeResizer(
        {
          heightCalculationMethod: 'taggedElement',
          widthCalculationMethod: 'documentElementScroll',
        },
        iframeEl,
      )
      iframeEl.onload = iframeLoadedHandler
      return undefined
    } else {
      const timeout = setTimeout(() => setHidden(false), WAIT_FOR_LOADING_TIMEOUT)
      const handler = (e: MessageEvent) => {
        if (e.data === 'BODY_START') {
          iframeLoadedHandler()
          clearTimeout(timeout)
        }
      }

      window.addEventListener('message', handler)
      return () => {
        clearTimeout(timeout)
        window.removeEventListener('message', handler)
      }
    }
  })

  return (
    <iframe
      data-tc-iframe-darktheme-adapter
      title={title}
      className={classNames(className, styles.iframe, hidden && styles.hidden)}
      ref={composeRefs(iframeRef, iframeInnerRef)}
      src={src}
      {...restProps}
    />
  )
}

export default React.memo(IframeAdapter)
